import { Cacheable } from '../shared/models/cacheable.model';
import { DocumentActivityType } from '../shared/mru/mru-plus-plus-v2.service';

// Constants
export const clientRequestIdHeader = 'client-request-id';
export const correlationIdResponseHeader = 'x-correlationid';
export const syncTimestampStorageKeyPrefix = `_Edgeworth${window.ewWorkload}LastSyncTimestamp`;
export const localeParam = 'rs=';

// Diagnostics
export interface EdgeworthPluginDiagnostics {
    localCount: number;
    localFailure: boolean;
    mruCount: number;
    mruFailure: boolean;
    wsCount: number;
    wsFailure: boolean;
}

export interface EdgeworthDebug {
    cid: string;
    files: Array<{id: string, title: string, extension: string}>;
    pluginData: EdgeworthPluginDiagnostics;
    api: string;
}

// Common
export interface BaseResponse {
    readonly diagnostics_data: Diagnostics;
    readonly is_complete?: boolean;
}

// Recent
export interface RawUserRelationship {
    readonly last_access_datetime?: string;
    readonly last_modified_datetime?: string;
    readonly last_shared_by_id?: string;
    readonly last_shared_datetime?: string;
    readonly last_shared_by_history?: ReadonlyArray<string>;
    readonly save_for_later_datetime?: string;
    readonly last_shared_display_name?: string;
    readonly last_action_datetime?: string;
}

export interface RawCreationInfo {
    readonly timestamp: string;
    readonly user?: {
        readonly display_name?: string;
        readonly upn?: string;
    };
}

export interface UserRelationship {
    save_for_later_datetime?: Date; // no readonly specifier so we can update on pin state change
    last_access_datetime?: Date; // no readonly specifier so we can update on pin state change
    readonly last_modified_datetime?: Date;
    readonly last_shared_by_id?: string;
    readonly last_shared_by_history?: ReadonlyArray<string>;
    readonly last_shared_datetime?: Date;
    readonly last_shared_display_name?: string;
    readonly last_action_datetime?: Date;
}

export interface MeetingArtifact {
    readonly id: string;
    readonly web_url?: string;
    readonly title?: string;
    readonly type?: string;
    readonly extension?: string;
}

export interface CreationInfo {
    readonly timestamp: Date;
    readonly user?: {
        readonly display_name?: string;
        readonly upn?: string;
    };
}

export interface OneDriveInfo {
    readonly drive_id: string;
    readonly item_id: string;
}

export interface SharePointInfo {
    readonly doc_id: number;
    readonly list_id: string;
    readonly site_id: string;
    readonly site_path: string;
    readonly unique_id: string;
    readonly web_id: string;
}

// 'LocalBridgeFile' == File from the HWA LocalBridge Process
// 'LocalFile' == File from AFS Local Files stored in Substrate
export type DocumentSource = 'LocalBridgeFile' | 'LocalFile' | 'OneDriveForBusiness' | 'SharePoint' | 'EmailAttachment';

export interface RawEdgeworthSubstrateFile {
    readonly activity_badge?: RawActivitiesBadge;
    readonly author: string;
    readonly container_title?: string;
    readonly container_url?: string;
    readonly created_datetime: string;
    readonly creation_info: RawCreationInfo;
    readonly data_source: ReadonlyArray<'Wset' | 'Mru' | 'LocalBridgeFile'>;
    readonly extension: string;
    readonly icon_url?: string;
    readonly id: string;
    readonly is_collab?: boolean;
    readonly is_deleted?: boolean;
    readonly is_pinned: boolean;
    readonly is_hidden: boolean;
    readonly is_redeemed?: boolean;
    readonly last_recommended_modified_datetime: string;
    readonly last_store_modified_datetime: string;
    readonly meeting_artifacts?: Array<MeetingArtifact>;
    readonly meetings?: ReadonlyArray<string>;
    readonly mru_id: string;
    readonly mru_type: string;
    readonly mru_url: string;
    readonly reference_type?: string;
    readonly onedrive_info: OneDriveInfo;
    readonly sharepoint_info: SharePointInfo;
    readonly sharing_reference_url?: string;
    readonly source: DocumentSource;
    readonly title: string;
    readonly type: string;
    readonly url: string;
    readonly user_relationship: RawUserRelationship;
    readonly web_url: string;
    readonly wset_id: string;
    readonly subject: string;
}

export interface EdgeworthSubstrateFile {
    readonly activity_badge?: ActivityBadge;
    readonly author: string;
    readonly container_title?: string;
    readonly container_url?: string;
    readonly created_datetime: Date;
    readonly creation_info: CreationInfo;
    readonly data_source: ReadonlyArray<'Wset' | 'Mru' | 'LocalBridgeFile'>;
    readonly extension: string;
    readonly id: string;
    readonly icon_url?: string;
    indexed: boolean;
    readonly is_collab?: boolean;
    readonly is_deleted?: boolean;
    is_pinned: boolean; // no readonly specifier so we can update on pin state change
    is_hidden: boolean; // no readonly specifier so we can update on hide state change
    readonly is_redeemed?: boolean;
    readonly last_recommended_modified_datetime: string;
    readonly last_store_modified_datetime: string;
    readonly meeting_artifacts?: Array<MeetingArtifact>;
    readonly meetings?: ReadonlyArray<string>;
    mru_id: string; // no readonly specifier so we can update while merging delta sync with memory cache
    mru_type: string; // no readonly specifier so we can update while merging delta sync with memory cache
    mru_url: string; // no readonly specifier so we can update while merging delta sync with memory cache
    readonly onedrive_info: OneDriveInfo;
    readonly reference_type?: string;
    readonly sharepoint_info: SharePointInfo;
    readonly sharing_reference_url?: string;
    readonly source: DocumentSource;
    readonly subject: string;
    readonly sub_type?: string;
    readonly title: string;
    readonly type: string;
    readonly teams_message_thread_id?: string;
    readonly url: string;
    readonly user_relationship: UserRelationship;
    readonly web_url: string;
    readonly wset_id: string;
}

export interface DiagnosticsExtendedInfo {
    readonly WorkingSetFilesPlugin: string;
    readonly MruFilesPlugin: string;
    readonly Method: string;
    readonly Version: string;
}

export interface Diagnostics {
    readonly build: string;
    readonly correlation_id: string;
    readonly data_center: string;
    readonly extended_info: DiagnosticsExtendedInfo;
    readonly http_status_code: number;
    readonly server_correlation_id: string;
    readonly server_name: string;
    readonly total_duration: number;
}

export interface ActivityBadge {
    readonly message_format: string;
    readonly users: ReadonlyArray<User>;
    readonly timestamp: Date;
    readonly type: DocumentActivityType;
}

export interface User {
    readonly id: string;
    readonly upn: string;
    readonly display_name: string;
}

export interface RawActivitiesBadge {
    readonly message_format: string;
    readonly users: ReadonlyArray<User>;
    readonly timestamp: string;
    readonly type: DocumentActivityType;
}

export interface RawEdgeworthSubstrateRecentResponseData extends BaseResponse {
    readonly files: ReadonlyArray<RawEdgeworthSubstrateFile>;
}

export interface EdgeworthSubstrateRecentResponseData extends BaseResponse {
    readonly files: ReadonlyArray<EdgeworthSubstrateFile>;
}

export interface EdgeworthSubstrateRecentData {
    readonly data: EdgeworthSubstrateRecentResponseData;
    readonly impressionId: string;
}

// DeltaSync
export interface EdgeworthSubstrateDeltaSyncFile extends EdgeworthSubstrateFile {
    activities?: ReadonlyArray<RecentActivity>; // no readonly specifier so they can be updated in-place (when deltasync has more information not in recent).
    readonly useractions_datetime?: Date;
    readonly useractions_datetime_inverse_time_in_ms?: number;
}

export interface RawRecentActivity {
    readonly a: string;
    readonly da: {
        readonly e: string;
        readonly i: string;
        readonly n: string;
        readonly t: string;
    };
    readonly r: string;
    readonly t: string;
}

export interface RawEdgeworthSubstrateDeltaSyncFile extends RawEdgeworthSubstrateFile {
    readonly activities?: ReadonlyArray<RawRecentActivity>;
}

export interface EdgeworthDataEvent {
    readonly items: ReadonlyArray<EdgeworthSubstrateDeltaSyncFile>;
    readonly hasPluginFailures: boolean;
}

export interface RawEdgeworthDeltaSyncResponse extends BaseResponse {
    readonly files: ReadonlyArray<RawEdgeworthSubstrateFile>;
    readonly next_page_link?: string;
}

// WorkingSet
export interface RecentActivity {
    /**
     * Document Actor
     */
    readonly da: DocumentActor;

    /**
     * Activity ID.
     */
    readonly r: string;

    /**
     * Timestamp.
     */
    readonly t: Date;

    /**
     * Item Activity.
     */
    a: RecentActivityAction;
}

interface DocumentActor {
    /**
     * User AAD ID.
     */
    readonly i: string;

    /**
     * User Name.
     */
    readonly n: string;

    /**
     * User Email.
     */
    readonly e: string;

    /**
     * User Last Activity Timestamp.
     */
    readonly t: Date;
}

interface ActionUser {
    readonly user: {
        readonly displayName: string;
        readonly userPrincipalName: string;
    };
}

interface MentionAction {
    readonly mentionees: ReadonlyArray<ActionUser>;
}

interface TaskAction {
    readonly assignees: ReadonlyArray<ActionUser>;
}

interface RecentActivityAction {
    readonly edit?: any;
    readonly mention?: MentionAction;
    readonly comment?: any;
    readonly taskCreated?: TaskAction;
    readonly taskReopened?: TaskAction;
    readonly taskReassigned?: TaskAction;
    readonly taskCompleted?: TaskAction;
    readonly version?: any;
}

interface RawRecentActivitesObject {
    readonly RecentActivities: string;
    readonly 'RecentActivitiesLastUpdated@odata.type': string;
    readonly RecentActivitiesLastUpdated: string;
}

interface RecentActivitesObject {
    readonly RecentActivities: ReadonlyArray<RecentActivity>;
    readonly 'RecentActivitiesLastUpdated@odata.type': string;
    readonly RecentActivitiesLastUpdated: Date;
}

interface SharePointItem {
    readonly DocId: number;
    readonly SitePath: string;
    readonly SiteId: string;
    readonly WebId: string;
    readonly ListId: string;
    readonly GroupId: string;
    readonly ListItemId: string;
    readonly ContentTypeId: string;
    readonly MediaDuration: number;
    readonly DefaultEncodingUrl: string;
    readonly ContentClass: string;
    readonly ModifiedBy: string;
    readonly ModifiedByDisplayName: string;
    readonly IsExternalContent: boolean;
    readonly FileUrl: string;
    readonly Region: string;
    readonly RestrictToRegion: boolean;
    readonly WebTemplate: string;
    readonly ParentId: string;
    readonly Language: string;
    readonly PromotedState: number;
    readonly EmailAddress: string;
    readonly UniqueId: string;
}

export interface RawEdgeworthDocument {
    readonly ContentSourceApplication: string;
    readonly Document: {
        readonly Author: string;
        readonly Title: string;
    };
    readonly FileExtension: string;
    readonly FileName: string;
    readonly FileModifiedTime: string;
    readonly FileCreatedTime: string;
    readonly Id: string;
    readonly UserRelationship: {
        readonly LastAccessDateTime?: string;
        readonly LastModifiedDateTime?: string;
        readonly LastSharedById?: string;
        readonly LastSharedDateTime?: string;
    };
    readonly ItemProperties: {
        readonly RecentActivities: RawRecentActivitesObject;
    };
    readonly SharePointItem: SharePointItem;
    readonly StorageProviderContext: number;
}

export interface EdgeworthDocument {
    readonly ContentSourceApplication: string;
    readonly Document: {
        readonly Author: string;
        readonly Title: string;
    };
    readonly FileExtension: string;
    readonly FileName: string;
    readonly FileModifiedTime: Date;
    readonly Id: string;
    readonly FileCreatedTime: Date;
    readonly UserRelationship: {
        readonly LastAccessDateTime?: Date;
        readonly LastModifiedDateTime?: Date;
        readonly LastSharedById?: string;
        readonly LastSharedDateTime?: Date;
        readonly SaveForLaterDateTime?: Date;
    };
    readonly ItemProperties: {
        readonly RecentActivities: RecentActivitesObject;
    };
    readonly SharePointItem: SharePointItem;
    readonly StorageProviderContext: number;
}

export interface RawEdgeworthData {
    readonly value: ReadonlyArray<RawEdgeworthDocument>;
}

export interface EdgeworthData extends Cacheable {
    readonly items: ReadonlyArray<EdgeworthDocument>;
    readonly impressionId: string;
}
